import { AdminApiServiceSection } from "./AdminApiServiceSection.js";
import { PersonaVoiceSection } from "./admin/PersonaVoiceSection.js";
import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";
import { api, ApiError } from "../api/client.js";

interface Workspace {
  readonly tenantId: string;
  readonly name: string;
  readonly status: string;
  readonly timezone: string;
  /**
   * Admin Workspaces default-view cleanliness (2026-09): real
   * Workspace<->User association (`GET /admin/workspaces`, backed by
   * `MembershipRepository.findByTenant`) — used ONLY to decide default
   * visibility below, joined against the already-fetched `/admin/users`
   * list. Never workspace ACCESS, never OWNER status, never recent
   * video activity, and NOT attribution (attribution is exclusively
   * `videos.initiated_by_user_id`, computed server-side, untouched by
   * this field).
   */
  readonly memberUserIds: readonly string[];
}

interface Plan {
  readonly id: string;
  readonly name: string;
  readonly entitlements: Record<string, unknown>;
}

interface AdminUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly isPlatformOwner: boolean;
  readonly emailVerified: boolean;
  readonly status: string;
  readonly suspendedAt: string | null;
  readonly createdAt: string;
}

interface ModelSummary {
  readonly modelId: string;
  readonly name: string;
  readonly basePrice: number | null;
  readonly description: string | null;
  readonly type: string | null;
}

interface PersonaReadiness {
  readonly personaId: string;
  readonly subjectKey: string;
  readonly displayName: string;
  readonly active: boolean;
  readonly activeReferenceCount: number;
  readonly availableIdentityStrategies: readonly string[];
  readonly ready: boolean;
  /** FIX-023 (2026-09-11): Admin's own Suspend/Reactivate decision — independent of `active` above and of `photaReadinessState` below. */
  readonly adminStatus: "active" | "suspended";
  readonly suspensionReason: string | null;
  /** FIX-023: truthful Phota readiness, derived server-side — `null` when this deployment hasn't wired Phota. "preparing" (2026-09-12): provider reported READY but the mandatory 15-minute preparation period hasn't elapsed yet. */
  readonly photaReadinessState: "collecting_references" | "training" | "preparing" | "ready" | "training_failed" | null;
  readonly photaProfileId: string | null;
}

const PHOTA_READINESS_STATE_LABEL: Readonly<Record<string, string>> = {
  collecting_references: "Collecting",
  training: "Training",
  preparing: "Preparing",
  ready: "Ready",
  training_failed: "Training failed",
};

interface License {
  readonly tenantId: string;
  readonly workspaceName: string;
  readonly licenseId: string | null;
  readonly planId: string | null;
  readonly planName: string | null;
  readonly status: string | null;
  readonly startedAt: string | null;
}

interface UsageRow {
  readonly tenantId: string;
  readonly workspaceName: string;
  readonly periodStart: string;
  readonly totalQuantity: number;
  readonly totalCostUsd: number | null;
  readonly byKind: Record<string, number>;
}

/**
 * Admin per-user/per-persona spend breakdown (2026-09, user-requested):
 * mirrors `GET /admin/users/:userId/persona-usage`'s response shape
 * exactly. `summary` is the four numbers shown inline on the user's own
 * row (always visible, no click needed); `byPersona` is the drill-down
 * breakdown revealed on expand — deliberately never a list of individual
 * videos, only per-persona averages/totals, per the explicit request.
 */
interface PersonaUsageSummary {
  readonly videoCount: number;
  readonly averageDurationSeconds: number | null;
  readonly averageCostPerVideoUsd: number | null;
  readonly totalCostUsd: number | null;
}

interface PersonaUsageRow extends PersonaUsageSummary {
  readonly tenantId: string;
  readonly workspaceName: string;
  readonly personaId: string;
  readonly personaDisplayName: string;
}

interface PersonaUsageResult {
  readonly summary: PersonaUsageSummary;
  readonly byPersona: readonly PersonaUsageRow[];
}

/**
 * Admin Cost Comparison (2026-09, user-requested): Video Studio vs.
 * FanStudio, mirroring `GET /admin/cost-comparison`'s response shape
 * exactly. Video Studio's numbers are FIX-014's own live, real
 * Production cost-efficiency calculation (never a second calculation);
 * FanStudio's numbers are Shai's own supplied example data, never a
 * live API — both `source` strings say so explicitly, verbatim from the
 * server, so this page never has to restate (and risk drifting from)
 * that provenance on its own.
 */
interface CostComparisonVideoStudio {
  readonly averageCostPerVideoUsd: number | null;
  readonly averageDurationSeconds: number | null;
  readonly averageCostPerSecondUsd: number | null;
  readonly includedVideoCount: number;
  /** FIX-030 (2026-10-04): the live metric's population — the latest 10 FINALIZED videos. Absent only on an older server, which still answers with the all-finalized average. */
  readonly population?: {
    readonly label: string;
    readonly windowSize: number;
    readonly videoCount: number;
    /** true = at least one of the videos has an incomplete cost or no duration: the figures count known values only and understate the real cost. */
    readonly partial: boolean;
    readonly partialVideoCount: number;
    readonly missingDurationCount: number;
  };
  readonly source: string;
}

/** FIX-030: while Cost Comparison is open the live Video Studio metric is re-read this often (the weekly-history table is NOT re-read). */
const COST_COMPARISON_REFRESH_MS = 30_000;

interface CostComparisonFanStudio {
  readonly planName: string;
  readonly planPriceUsd: number;
  readonly creditsPerMonth: number;
  readonly observedVideoCredits: number;
  readonly observedDurationSeconds: number;
  readonly nominalCostPerVideoUsd: number;
  readonly nominalCostPerSecondUsd: number;
  readonly receiptEffectiveCostPerCreditUsd: number;
  readonly receiptAmountPaidUsd: number;
  readonly receiptDatePaid: string;
  readonly source: string;
}

type CheaperSystem = "video-studio" | "fanstudio" | "tie";

interface CostComparisonResult {
  readonly generatedAt: string;
  readonly videoStudio: CostComparisonVideoStudio;
  readonly fanStudio: CostComparisonFanStudio;
  readonly comparison: {
    readonly percentageDifference: number | null;
    readonly cheaperSystem: CheaperSystem | null;
    readonly basis: string;
  };
}

interface CostComparisonSnapshotRow {
  readonly id: string;
  readonly snapshotDate: string;
  readonly videoStudioAvgCostPerVideoUsd: number | null;
  readonly videoStudioAvgDurationSeconds: number | null;
  readonly videoStudioCostPerSecondUsd: number | null;
  readonly videoStudioIncludedVideoCount: number;
  readonly fanStudioCostPerVideoUsd: number;
  readonly fanStudioCostPerSecondUsd: number;
  readonly percentageDifference: number | null;
  readonly cheaperSystem: CheaperSystem | null;
  readonly createdAt: string;
}

/**
 * Admin video/image cost collection (2026-09, user-requested): mirrors
 * `GET /admin/videos/recent-cost`'s response shape exactly. Deliberately
 * separate from `PersonaUsageSummary`/`CostComparisonVideoStudio` above —
 * this is its OWN metric ("last 10 videos", platform-wide, every status),
 * never mixed with either of those.
 */
/** Mirrors `GET /admin/costs/last-30-days` (2026-10). */
interface TotalCostCategory {
  readonly key: string;
  readonly label: string;
  readonly amountUsd: number;
  readonly pendingCount: number;
}
interface TotalCostLast30DaysResult {
  readonly generatedAt: string;
  readonly since: string;
  readonly totalCostUsd: number;
  readonly pendingCount: number;
  readonly workspaceCount: number;
  readonly deepResearch: { readonly runs: number; readonly rapidapiCalls: number; readonly rapidapiBasis: string };
  readonly failedUnreconciled?: { readonly count: number; readonly estimatedUsd: number };
  readonly categories: readonly TotalCostCategory[];
  readonly notRecorded: readonly string[];
}
/** Mirrors `GET /admin/research/recent-cost` (2026-10). */
interface RecentResearchCostRun {
  readonly id: string;
  readonly kind: string;
  readonly status: string;
  readonly createdAt: string;
  readonly rapidapiCalls: number;
  readonly rapidapiUsd: number;
  readonly geminiUsd: number;
  readonly totalCostUsd: number;
}
interface RecentResearchCostResult {
  readonly generatedAt: string;
  readonly rapidapiUsdPerCall: number;
  readonly summary: { readonly runCount: number; readonly averageCostPerRunUsd: number | null; readonly totalCostUsd: number };
  readonly runs: readonly RecentResearchCostRun[];
}

interface RecentVideoCostRow {
  readonly id: string;
  readonly createdAt: string;
  readonly status: string;
  readonly totalCostUsd: number;
  readonly partial: boolean;
  /** Owner t765: the video's length (canonical final job), when known. */
  readonly durationSeconds?: number | null;
}

interface RecentVideoCostResult {
  readonly generatedAt: string;
  /** `re` / `rp` when the request filtered by production method (2026-10 RE/RP split). */
  readonly origin?: "re" | "rp" | "all";
  readonly summary: {
    readonly videoCount: number;
    readonly averageCostPerVideoUsd: number | null;
    readonly partial: boolean;
    /** Owner t765: average length of the SAME videos the cost average uses (only those with a known length). */
    readonly averageDurationSeconds?: number | null;
    readonly durationVideoCount?: number;
  };
  readonly videos: readonly RecentVideoCostRow[];
}

/** Mirrors `GET /admin/images/recent-slot-cost`'s response shape exactly. Unit = Slot, never the batch or a bare "image" count — see the route's own doc comment. */
interface RecentImageSlotCostRow {
  readonly batchId: string;
  readonly slotNumber: number;
  readonly createdAt: string;
  readonly status: string;
  readonly imagesGeneratedCount: number;
  readonly slotTotalCostUsd: number;
  readonly averageCostPerImageUsd: number | null;
  readonly partial: boolean;
}

interface RecentImageSlotCostResult {
  readonly generatedAt: string;
  readonly summary: {
    readonly slotCount: number;
    readonly averageCostPerImageUsd: number | null;
    readonly partial: boolean;
  };
  readonly slots: readonly RecentImageSlotCostRow[];
}

function cheaperSystemLabel(value: CheaperSystem | null): string {
  if (value === "video-studio") return "Video Studio";
  if (value === "fanstudio") return "FanStudio";
  if (value === "tie") return "Tie";
  return "—";
}

interface AuditEntry {
  readonly id: string;
  readonly actorUserId: string | null;
  readonly tenantId: string | null;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string | null;
  readonly result: string;
  readonly createdAt: string;
}

/**
 * Admin Users default-view cleanliness (2026-09): `UserRecord` has no
 * explicit test/fixture classification field anywhere in the schema
 * (verified directly — id/email/passwordHash/displayName/isPlatformOwner/
 * emailVerifiedAt/status/suspendedAt/createdAt/updatedAt, nothing else).
 * Absent a real field, this is the smallest truthful, read-only
 * classification available: the exact local-part prefixes already
 * confirmed in Production to be FIX-acceptance/smoke-test/reset-token
 * fixtures, plus the two IANA/RFC-2606-reserved domains
 * (`example.com` and any `*.test` host) that can never be used by a
 * real customer signup in the first place — not a broad content
 * heuristic, a hard reserved-namespace fact. Deliberately narrow: a
 * real user's own gmail.com/etc. address never matches any of this.
 * Never persisted, never affects `status` or any other stored field —
 * display filtering only.
 */
const TEST_FIXTURE_EMAIL_LOCAL_PART_PREFIXES = [
  "reset-token-test-",
  "phase-a-smoketest-",
  "phase-b-invitee-",
  "remediation-acceptance-",
  "fix002-test-",
  "fix009-test-",
  "fixbatch-",
] as const;

function isTestFixtureAccountEmail(email: string): boolean {
  const lower = email.toLowerCase();
  const atIndex = lower.lastIndexOf("@");
  if (atIndex === -1) return false;
  const localPart = lower.slice(0, atIndex);
  const domain = lower.slice(atIndex + 1);
  const matchesKnownFixturePrefix = TEST_FIXTURE_EMAIL_LOCAL_PART_PREFIXES.some((prefix) => localPart.startsWith(prefix));
  const isReservedTestDomain = domain === "example.com" || domain.endsWith(".test");
  return matchesKnownFixturePrefix || isReservedTestDomain;
}

/** FIX-050 stage 5: the exact summary the API returns from the canonical RunLinkScaleFanvueAttribution use case. */
interface AttributionRunResult {
  readonly candidates: number;
  readonly distinctSids: number;
  readonly matchedSids: number;
  readonly verified: number;
  readonly newlyInserted: number;
  readonly alreadyPresent: number;
  readonly unmatched: number;
  readonly conflicts: number;
  readonly conflictsByReason: Readonly<Record<string, number>>;
  readonly verifiedByBucket: Readonly<Record<string, number>>;
  readonly dataQuality: {
    readonly fanvueMetadataRows: number;
    readonly metadataRowsWithSid: number;
    readonly distinctSids: number;
    readonly linkscaleClickRows: number;
    readonly distinctStatsIds: number;
    readonly statsIdsWithMultipleRows: number;
    readonly maxRowsPerStatsId: number;
    readonly statsIdsWithoutFanvueSid: number;
  };
}

function formatCounts(counts: Readonly<Record<string, number>>): string {
  const entries = Object.entries(counts);
  return entries.length === 0 ? "none" : entries.map(([key, count]) => `${key}: ${count}`).join(", ");
}

function Section({ title, children }: { readonly title: string; readonly children: React.ReactNode }) {
  return (
    <div className="card">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

function NotConfigured({ label }: { readonly label: string }) {
  return <p className="muted">{label}: Not configured.</p>;
}

/** Owner visibility (t726u): how many `/admin/users/:id/persona-usage` requests run at once (each one walks every GENERATE workspace server-side). */
export const PERSONA_USAGE_CONCURRENCY = 4;

export function AdminPage() {
  const { user, csrfToken } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [licenses, setLicenses] = useState<License[] | null>(null);
  const [usage, setUsage] = useState<UsageRow[] | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [auditLog, setAuditLog] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assignTenantId, setAssignTenantId] = useState("");
  const [assignPlanId, setAssignPlanId] = useState("");
  const [assignStatus, setAssignStatus] = useState<"trialing" | "active" | "past_due" | "canceled">("trialing");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);
  const [plansNote, setPlansNote] = useState<string | null>(null);
  const [models, setModels] = useState<ModelSummary[] | null>(null);
  const [personaWorkspaceId, setPersonaWorkspaceId] = useState("");
  const [personas, setPersonas] = useState<PersonaReadiness[] | null>(null);
  const [personasError, setPersonasError] = useState<string | null>(null);
  const [statusActionError, setStatusActionError] = useState<string | null>(null);
  const [personaUsageByUserId, setPersonaUsageByUserId] = useState<Record<string, PersonaUsageResult | null>>({});
  const [expandedUserIds, setExpandedUserIds] = useState<ReadonlySet<string>>(new Set());
  // Admin Users default-view cleanliness (2026-09): display filter only —
  // `users` itself (and every metric derived from it, including the
  // per-user persona-usage fetch effect below) is untouched by this
  // toggle. Default off: hide suspended + confirmed test/fixture rows.
  const [showTestOrSuspendedAccounts, setShowTestOrSuspendedAccounts] = useState(false);
  // Admin Cost Comparison (2026-09): loaded on demand, like the Personas
  // section above — the live comparison loops every real GENERATE
  // workspace, so it is never fetched eagerly in `loadAll` just because
  // the OWNER opened the Admin page. `costComparisonOpen` is the "exactly
  // one button" entry point Shai asked for; everything below it only
  // renders once that button has been clicked.
  const [costComparisonOpen, setCostComparisonOpen] = useState(false);
  const [costComparisonLoading, setCostComparisonLoading] = useState(false);
  const [costComparison, setCostComparison] = useState<CostComparisonResult | null>(null);
  const [costComparisonHistory, setCostComparisonHistory] = useState<readonly CostComparisonSnapshotRow[] | null>(null);
  const [costComparisonError, setCostComparisonError] = useState<string | null>(null);
  // FIX-030: one live-metric request at a time (the initial load and the 30 s refresh never overlap).
  const costComparisonInFlightRef = useRef(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [snapshotSuccess, setSnapshotSuccess] = useState<string | null>(null);
  // FIX-050 stage 5 (2026-10-01): manual-only "Run Verified Attribution" — no scheduler, no automation.
  const [attributionRunning, setAttributionRunning] = useState(false);
  const attributionRunningRef = useRef(false);
  const [attributionResult, setAttributionResult] = useState<AttributionRunResult | null>(null);
  const [attributionError, setAttributionError] = useState<string | null>(null);
  // Admin video/image cost collection (2026-09): both fetched fresh in
  // `loadAll` (never cached across an Admin open, same doctrine as every
  // other section below) so the two main averages are available the
  // moment the page opens, per the explicit requirement — clicking a
  // metric only toggles whether its already-fetched 10-row detail is
  // SHOWN, it never re-fetches (the detail is exactly as fresh as the
  // summary it came from, both from the same page-open request).
  // `undefined` = still loading, `null` = checked, unavailable for this
  // deployment (404 — rendered as "—", never a bug).
  const [recentRpVideoCost, setRecentRpVideoCost] = useState<RecentVideoCostResult | null | undefined>(undefined);
  const [recentReVideoCost, setRecentReVideoCost] = useState<RecentVideoCostResult | null | undefined>(undefined);
  const [totalCost30d, setTotalCost30d] = useState<TotalCostLast30DaysResult | null | undefined>(undefined);
  const [recentResearchCost, setRecentResearchCost] = useState<RecentResearchCostResult | null | undefined>(undefined);
  const [recentImageSlotCost, setRecentImageSlotCost] = useState<RecentImageSlotCostResult | null | undefined>(undefined);

  const loadAll = useCallback(() => {
    Promise.all([
      api.get<{ workspaces: Workspace[] }>("/admin/workspaces"),
      api.get<{ plans: Plan[]; managementNote: string }>("/admin/plans"),
      api.get<{ entries: AuditEntry[] }>("/admin/audit-log"),
      api.get<{ users: AdminUser[] }>("/admin/users"),
      api.get<{ licenses: License[] }>("/admin/licenses").catch(() => ({ licenses: null as unknown as License[] })),
      api.get<{ usage: UsageRow[] }>("/admin/usage").catch(() => ({ usage: null as unknown as UsageRow[] })),
      api.get<{ models: ModelSummary[] }>("/admin/models").catch(() => ({ models: null as unknown as ModelSummary[] })),
      // Admin video/image cost collection (2026-09): fetched here (not
      // gated behind a button, unlike Cost Comparison below) so both
      // averages are on screen the instant Admin opens. A 404 (feature
      // not wired for this deployment) or any other failure resolves to
      // `null`, rendered as "—" — never left on a spinner, never treated
      // as "$0 cost".
      api.get<RecentVideoCostResult>("/admin/videos/recent-cost?origin=rp").catch(() => null),
      api.get<RecentVideoCostResult>("/admin/videos/recent-cost?origin=re").catch(() => null),
      api.get<RecentImageSlotCostResult>("/admin/images/recent-slot-cost").catch(() => null),
      api.get<TotalCostLast30DaysResult>("/admin/costs/last-30-days").catch(() => null),
      api.get<RecentResearchCostResult>("/admin/research/recent-cost").catch(() => null),
    ])
      .then(([workspaceResult, planResult, auditResult, userResult, licenseResult, usageResult, modelsResult, recentRpVideoCostResult, recentReVideoCostResult, recentImageSlotCostResult, totalCost30dResult, recentResearchCostResult]) => {
        setWorkspaces(workspaceResult.workspaces);
        setPlans(planResult.plans);
        setPlansNote(planResult.managementNote);
        setAuditLog(auditResult.entries);
        setUsers(userResult.users);
        setLicenses(licenseResult.licenses);
        setUsage(usageResult.usage);
        setModels(modelsResult.models);
        setRecentRpVideoCost(recentRpVideoCostResult);
        setRecentReVideoCost(recentReVideoCostResult);
        setTotalCost30d(totalCost30dResult);
        setRecentResearchCost(recentResearchCostResult);
        setRecentImageSlotCost(recentImageSlotCostResult);
      })
      .catch((loadError) => setError(loadError instanceof ApiError ? loadError.message : "Failed to load Admin data."));
  }, []);

  useEffect(() => {
    if (!user?.isPlatformOwner) return;
    loadAll();
  }, [user, loadAll]);

  // Admin per-user/per-persona spend breakdown (2026-09): one GET per
  // user, fired once the user list itself has loaded. A 404 (feature not
  // wired for this deployment) or any other failure resolves to `null`
  // for that user — rendered as "—", never left stuck on a spinner and
  // never treated as "zero usage".
  // Owner visibility (t726u): each request is server-side heavy (it walks every GENERATE workspace), so firing one per user all at once
  // (94+ in parallel) against the API's small shared DB pool kept EVERY row on "—" until the slowest one finished (the old Promise.all was
  // all-or-nothing). Now at most PERSONA_USAGE_CONCURRENCY run at a time and each row fills in as soon as its own answer arrives; a row still
  // loading shows "…" (title "Loading"), a failed one keeps the truthful "—". Read-only, OWNER-only, same endpoint, same numbers.
  useEffect(() => {
    if (user?.isPlatformOwner !== true || users === null) return;
    let cancelled = false;
    const queue = users.map((u) => u.id);
    const worker = async (): Promise<void> => {
      for (let id = queue.shift(); id !== undefined && !cancelled; id = queue.shift()) {
        const userId = id;
        const result = await api
          .get<PersonaUsageResult>(`/admin/users/${encodeURIComponent(userId)}/persona-usage`)
          .catch((): PersonaUsageResult | null => null);
        if (!cancelled) setPersonaUsageByUserId((previous) => ({ ...previous, [userId]: result }));
      }
    };
    void Promise.all(Array.from({ length: Math.min(PERSONA_USAGE_CONCURRENCY, queue.length) }, () => worker()));
    return () => {
      cancelled = true;
    };
  }, [user?.isPlatformOwner, users]);

  function toggleExpandedUser(userId: string): void {
    setExpandedUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function formatUsd(value: number | null): string {
    return value === null ? "—" : `$${value.toFixed(2)}`;
  }
  const recentCostUpdatedAt: string | null =
    [recentRpVideoCost, recentReVideoCost, recentImageSlotCost, recentResearchCost]
      .map((r) => (r === undefined || r === null ? null : r.generatedAt))
      .filter((t): t is string => t !== null)
      .sort()
      .at(-1) ?? null;
  // "Average cost — last 10 of each type" (2026-10): one simple tile per production type, same cost rules as the API endpoints.
  function renderCostTile<T>(
    testId: string,
    label: string,
    result: T | null | undefined,
    pick: (r: T) => { readonly average: number | null; readonly count: number; readonly unit: string; readonly partial: boolean; readonly avgLengthSeconds?: number | null; readonly lengthCount?: number },
  ) {
    const value = result === undefined || result === null ? null : pick(result);
    return (
      <div className="cost-tile" data-testid={testId}>
        <div className="cost-tile-label">{label}</div>
        <div className="cost-tile-value">
          {result === undefined ? "…" : value === null ? "—" : formatUsd(value.average)}
          {value?.partial && <span className="badge status-rejected" style={{ marginLeft: 6 }}>Partial</span>}
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {result === undefined ? "Loading…" : value === null ? "Not configured" : `${value.count} ${value.unit}${value.count === 1 ? "" : "s"} included`}
        </div>
        {value !== null && value.avgLengthSeconds !== undefined && value.avgLengthSeconds !== null && (
          <div className="muted cost-tile-length" style={{ fontSize: 12 }}>avg length {value.avgLengthSeconds.toFixed(1)}s{value.lengthCount !== undefined && value.lengthCount < value.count ? ` (${value.lengthCount} of ${value.count})` : ""}</div>
        )}
      </div>
    );
  }



  function formatSeconds(value: number | null): string {
    return value === null ? "—" : `${value.toFixed(1)}s`;
  }

  function formatPercent(value: number | null): string {
    return value === null ? "—" : `${value.toFixed(1)}%`;
  }

  async function handleAssignLicense(event: FormEvent): Promise<void> {
    event.preventDefault();
    setAssignError(null);
    setAssignSuccess(null);
    setAssigning(true);
    try {
      await api.post(`/admin/tenants/${encodeURIComponent(assignTenantId)}/license`, csrfToken, { planId: assignPlanId, status: assignStatus });
      setAssignSuccess(`Assigned ${assignPlanId} (${assignStatus}) to ${assignTenantId}.`);
      loadAll();
    } catch (err) {
      setAssignError(err instanceof ApiError ? err.message : "Could not assign the license — please try again.");
    } finally {
      setAssigning(false);
    }
  }

  async function handleSetUserStatus(userId: string, action: "suspend" | "reactivate"): Promise<void> {
    setStatusActionError(null);
    try {
      await api.post(`/admin/users/${encodeURIComponent(userId)}/${action}`, csrfToken, {});
      loadAll();
    } catch (err) {
      setStatusActionError(err instanceof ApiError ? err.message : `Could not ${action} the user — please try again.`);
    }
  }

  async function handleVerifyUserEmail(userId: string): Promise<void> {
    setStatusActionError(null);
    try {
      await api.post(`/admin/users/${encodeURIComponent(userId)}/verify-email`, csrfToken, {});
      loadAll();
    } catch (err) {
      setStatusActionError(err instanceof ApiError ? err.message : "Could not verify this user's email — please try again.");
    }
  }

  async function handleSetWorkspaceStatus(tenantId: string, action: "suspend" | "reactivate"): Promise<void> {
    setStatusActionError(null);
    try {
      await api.post(`/admin/workspaces/${encodeURIComponent(tenantId)}/${action}`, csrfToken, {});
      loadAll();
    } catch (err) {
      setStatusActionError(err instanceof ApiError ? err.message : `Could not ${action} the workspace — please try again.`);
    }
  }

  async function reloadPersonas(): Promise<void> {
    setPersonasError(null);
    try {
      const result = await api.get<{ personas: PersonaReadiness[] }>(`/admin/workspaces/${encodeURIComponent(personaWorkspaceId)}/personas`);
      setPersonas(result.personas);
    } catch (err) {
      setPersonasError(err instanceof ApiError ? err.message : "Could not load personas for that workspace.");
    }
  }

  async function handleLoadPersonas(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPersonas(null);
    await reloadPersonas();
  }

  /**
   * FIX-023 (Persona onboarding/management, 2026-09-11): Persona-level
   * Suspend/Reactivate — same pattern as `handleWorkspaceStatusAction`
   * just above (workspace suspend/reactivate), reusing the existing
   * Admin action-then-reload shape rather than a new UI concept.
   */
  async function handlePersonaStatusAction(personaId: string, action: "suspend" | "reactivate"): Promise<void> {
    setPersonasError(null);
    try {
      await api.post(`/admin/personas/${encodeURIComponent(personaId)}/${action}`, csrfToken, { tenantId: personaWorkspaceId });
      await reloadPersonas();
    } catch (err) {
      setPersonasError(err instanceof ApiError ? err.message : `Could not ${action} the persona — please try again.`);
    }
  }

  async function loadCostComparison(): Promise<void> {
    costComparisonInFlightRef.current = true;
    setCostComparisonLoading(true);
    setCostComparisonError(null);
    try {
      const [comparisonResult, historyResult] = await Promise.all([
        api.get<CostComparisonResult>("/admin/cost-comparison"),
        api.get<{ snapshots: CostComparisonSnapshotRow[] }>("/admin/cost-comparison/history"),
      ]);
      setCostComparison(comparisonResult);
      setCostComparisonHistory(historyResult.snapshots);
    } catch (err) {
      setCostComparisonError(err instanceof ApiError ? err.message : "Could not load the cost comparison — please try again.");
    } finally {
      costComparisonInFlightRef.current = false;
      setCostComparisonLoading(false);
    }
  }

  // FIX-030: re-reads ONLY the live Video Studio metric (one GET, recomputed server-side from the current records) — never the weekly-history snapshots, never a spinner.
  // A failed refresh keeps the numbers on screen and says so; the next successful one clears the message.
  const refreshLiveCostComparison = useCallback(async (): Promise<void> => {
    if (costComparisonInFlightRef.current) {
      return;
    }
    costComparisonInFlightRef.current = true;
    try {
      const result = await api.get<CostComparisonResult>("/admin/cost-comparison");
      setCostComparison(result);
      setCostComparisonError(null);
    } catch (err) {
      setCostComparisonError(err instanceof ApiError ? err.message : "Could not refresh the cost comparison — showing the last values.");
    } finally {
      costComparisonInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!costComparisonOpen) {
      return undefined;
    }
    const timer = setInterval(() => {
      void refreshLiveCostComparison();
    }, COST_COMPARISON_REFRESH_MS);
    return () => clearInterval(timer);
  }, [costComparisonOpen, refreshLiveCostComparison]);

  function handleOpenCostComparison(): void {
    setCostComparisonOpen(true);
    void loadCostComparison();
  }

  async function handleSaveCostComparisonSnapshot(): Promise<void> {
    setSnapshotError(null);
    setSnapshotSuccess(null);
    setSavingSnapshot(true);
    try {
      const result = await api.post<{ snapshotDate: string }>("/admin/cost-comparison/snapshot", csrfToken, {});
      setSnapshotSuccess(`Saved the snapshot for ${result.snapshotDate}.`);
      const historyResult = await api.get<{ snapshots: CostComparisonSnapshotRow[] }>("/admin/cost-comparison/history");
      setCostComparisonHistory(historyResult.snapshots);
    } catch (err) {
      setSnapshotError(err instanceof ApiError ? err.message : "Could not save this week's snapshot — please try again.");
    } finally {
      setSavingSnapshot(false);
    }
  }

  async function handleRunVerifiedAttribution(): Promise<void> {
    // Hard guard against a double submit: the button is disabled while running, and this also covers a second call
    // arriving before React has re-rendered.
    if (attributionRunningRef.current) {
      return;
    }
    attributionRunningRef.current = true;
    setAttributionRunning(true);
    setAttributionError(null);
    setAttributionResult(null);
    try {
      const response = await api.post<{ result: AttributionRunResult }>("/admin/linkscale-attribution/run", csrfToken, {});
      setAttributionResult(response.result);
    } catch (err) {
      setAttributionError(err instanceof ApiError ? err.message : "The attribution run failed — please try again.");
    } finally {
      attributionRunningRef.current = false;
      setAttributionRunning(false);
    }
  }

  if (!user?.isPlatformOwner) {
    return <Navigate to="/generate" replace />;
  }

  // Admin Users default-view cleanliness (2026-09): a display-only
  // derivation of `users` — the underlying state, and every metric
  // computed from it, is never touched by this filter.
  const visibleUsers =
    users === null ? null : users.filter((u) => showTestOrSuspendedAccounts || (u.status !== "suspended" && !isTestFixtureAccountEmail(u.email)));
  const hiddenUserCount = users === null || visibleUsers === null ? 0 : users.length - visibleUsers.length;

  // Admin Workspaces default-view cleanliness (2026-09, follow-up to the
  // Users cleanup above): a Workspace is visible by default only if it
  // is actually ASSOCIATED (via `memberUserIds`, real membership data)
  // with at least one real, active, non-fixture user — reusing the
  // EXACT SAME `isTestFixtureAccountEmail` classification and the SAME
  // toggle, never a second one. Deliberately NOT decided by workspace
  // name, `is_generate_workspace`, OWNER's own administrative access
  // (OWNER is never itself a member of a workspace it didn't join), or
  // recent video activity — only real Workspace<->User association.
  const usersById = new Map((users ?? []).map((u) => [u.id, u]));
  const visibleWorkspaces =
    workspaces === null || users === null
      ? null
      : workspaces.filter(
          (w) =>
            showTestOrSuspendedAccounts ||
            w.memberUserIds.some((id) => {
              const member = usersById.get(id);
              return member !== undefined && member.status !== "suspended" && !isTestFixtureAccountEmail(member.email);
            }),
        );
  const hiddenWorkspaceCount = workspaces === null || visibleWorkspaces === null ? 0 : workspaces.length - visibleWorkspaces.length;

  return (
    <div>
      <div className="card cost-total-card" data-testid="total-cost-30d">
        <h2>Total cost — last 30 days (all included)</h2>
        {totalCost30d === undefined ? (
          <p className="muted">Loading…</p>
        ) : totalCost30d === null ? (
          <p className="muted">Not configured.</p>
        ) : (
          <>
            <p className="cost-total-amount">
              {formatUsd(totalCost30d.totalCostUsd)}
              {totalCost30d.pendingCount > 0 && (
                <span className="badge status-processing" style={{ marginLeft: 8 }} title="Counted at the estimate until billing is reconciled">
                  {totalCost30d.pendingCount} pending
                </span>
              )}
            </p>
            <p className="muted" style={{ fontSize: 12 }}>
              {totalCost30d.workspaceCount} workspace{totalCost30d.workspaceCount === 1 ? "" : "s"} · since {new Date(totalCost30d.since).toLocaleDateString()} · updated{" "}
              {new Date(totalCost30d.generatedAt).toLocaleString()}
            </p>
            <ul className="cost-breakdown">
              {totalCost30d.categories
                .filter((category) => category.amountUsd > 0)
                .map((category) => (
                  <li key={category.key}>
                    <span>{category.label}</span>
                    <strong>{formatUsd(category.amountUsd)}</strong>
                  </li>
                ))}
            </ul>
            <p className="muted" style={{ fontSize: 12 }}>
              Deep Research RapidAPI is counted as the fixed $9.99 monthly subscription ({totalCost30d.deepResearch.rapidapiCalls} calls in this period).
            </p>
            {(totalCost30d.failedUnreconciled?.count ?? 0) > 0 && (
              <p className="muted" style={{ fontSize: 12 }}>
                Not counted: {totalCost30d.failedUnreconciled!.count} failed job{totalCost30d.failedUnreconciled!.count === 1 ? "" : "s"} with no billed cost recorded (estimate{" "}
                {formatUsd(totalCost30d.failedUnreconciled!.estimatedUsd)}).
              </p>
            )}
            <p className="muted" style={{ fontSize: 12 }}>
              Not included (not recorded): {totalCost30d.notRecorded.join("; ")}.
            </p>
          </>
        )}
      </div>
      <h1>Admin — Owner Control Center</h1>
      {error && <div className="error-banner">{error}</div>}

      <Section title="Users">
        {statusActionError && <div className="error-banner">{statusActionError}</div>}
        <p className="field" style={{ marginBottom: 12 }}>
          <label>
            <input
              type="checkbox"
              checked={showTestOrSuspendedAccounts}
              onChange={(event) => setShowTestOrSuspendedAccounts(event.target.checked)}
            />{" "}
            Show test/suspended accounts &amp; workspaces
          </label>
          {!showTestOrSuspendedAccounts && hiddenUserCount > 0 && (
            <span className="muted" style={{ marginLeft: 8 }}>
              ({hiddenUserCount} user{hiddenUserCount === 1 ? "" : "s"} hidden)
            </span>
          )}
        </p>
        {visibleUsers === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Email</th>
                <th>Name</th>
                <th>Verified</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Created</th>
                <th>Videos</th>
                <th>Avg duration</th>
                <th>Avg cost/video</th>
                <th>Total spend</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((u) => {
                const personaUsage = personaUsageByUserId[u.id];
                const summary = personaUsage?.summary;
                const isExpanded = expandedUserIds.has(u.id);
                const canExpand = personaUsage !== undefined && personaUsage !== null && personaUsage.byPersona.length > 0;
                // FIX-045 (Admin: View external user/workspace History): a user
                // row has no single tenantId of its own (their persona-usage
                // summary can span multiple workspaces) — never guess. Only
                // offer the link here when this user belongs to EXACTLY one
                // workspace (via the already-fetched `/admin/workspaces`
                // list's `memberUserIds`); otherwise the Workspaces table
                // below is the reliable, always-unambiguous path.
                const memberWorkspaces = workspaces?.filter((w) => w.memberUserIds.includes(u.id)) ?? [];
                const singleWorkspaceTenantId = memberWorkspaces.length === 1 ? memberWorkspaces[0]!.tenantId : null;
                return (
                  <Fragment key={u.id}>
                    <tr>
                      <td>
                        {canExpand && (
                          <button type="button" onClick={() => toggleExpandedUser(u.id)} aria-label={isExpanded ? "Collapse persona breakdown" : "Expand persona breakdown"}>
                            {isExpanded ? "▾" : "▸"}
                          </button>
                        )}
                      </td>
                      <td>{u.email}</td>
                      <td>{u.displayName ?? "—"}</td>
                      <td>{u.emailVerified ? "Yes" : "No"}</td>
                      <td>{u.isPlatformOwner ? "Yes" : ""}</td>
                      <td>{u.status}</td>
                      <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                      <td>{personaUsage === undefined ? <span title="Loading">…</span> : (summary?.videoCount ?? "—")}</td>
                      <td>{personaUsage === undefined ? <span title="Loading">…</span> : formatSeconds(summary?.averageDurationSeconds ?? null)}</td>
                      <td>{personaUsage === undefined ? <span title="Loading">…</span> : formatUsd(summary?.averageCostPerVideoUsd ?? null)}</td>
                      <td>{personaUsage === undefined ? <span title="Loading">…</span> : formatUsd(summary?.totalCostUsd ?? null)}</td>
                      <td>
                        {singleWorkspaceTenantId !== null && (
                          <a href={`/admin/workspaces/${encodeURIComponent(singleWorkspaceTenantId)}/history`} style={{ marginRight: 8 }}>
                            View History
                          </a>
                        )}
                        {u.isPlatformOwner ? (
                          <span className="muted">—</span>
                        ) : (
                          <>
                            {!u.emailVerified && (
                              <button type="button" onClick={() => void handleVerifyUserEmail(u.id)} style={{ marginRight: 8 }}>
                                Verify email
                              </button>
                            )}
                            {u.status === "suspended" ? (
                              <button type="button" onClick={() => void handleSetUserStatus(u.id, "reactivate")}>
                                Reactivate
                              </button>
                            ) : (
                              <button type="button" onClick={() => void handleSetUserStatus(u.id, "suspend")}>
                                Suspend
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                    {isExpanded && personaUsage && (
                      <tr>
                        <td></td>
                        <td colSpan={11}>
                          <table>
                            <thead>
                              <tr>
                                <th>Persona</th>
                                <th>Workspace</th>
                                <th>Videos</th>
                                <th>Avg duration</th>
                                <th>Avg cost/video</th>
                                <th>Total spend</th>
                              </tr>
                            </thead>
                            <tbody>
                              {personaUsage.byPersona.map((row) => (
                                <tr key={`${row.tenantId}-${row.personaId}`}>
                                  <td>{row.personaDisplayName}</td>
                                  <td>{row.workspaceName}</td>
                                  <td>{row.videoCount}</td>
                                  <td>{formatSeconds(row.averageDurationSeconds)}</td>
                                  <td>{formatUsd(row.averageCostPerVideoUsd)}</td>
                                  <td>{formatUsd(row.totalCostUsd)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={12} className="muted">
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Workspaces">
        {statusActionError && <div className="error-banner">{statusActionError}</div>}
        {!showTestOrSuspendedAccounts && hiddenWorkspaceCount > 0 && (
          <p className="muted" style={{ marginTop: -8, marginBottom: 12 }}>
            ({hiddenWorkspaceCount} workspace{hiddenWorkspaceCount === 1 ? "" : "s"} hidden — no associated real active user. Use "Show
            test/suspended accounts &amp; workspaces" above to reveal them.)
          </p>
        )}
        {visibleWorkspaces === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Timezone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleWorkspaces.map((w) => (
                <tr key={w.tenantId}>
                  <td>{w.tenantId}</td>
                  <td>{w.name}</td>
                  <td>{w.status}</td>
                  <td>{w.timezone}</td>
                  <td>
                    <a href={`/admin/workspaces/${encodeURIComponent(w.tenantId)}/history`}>View History</a>{" "}
                    {w.status === "suspended" ? (
                      <button type="button" onClick={() => void handleSetWorkspaceStatus(w.tenantId, "reactivate")}>
                        Reactivate
                      </button>
                    ) : (
                      <button type="button" onClick={() => void handleSetWorkspaceStatus(w.tenantId, "suspend")}>
                        Suspend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <div className="card" data-testid="recent-cost-card">
        <h2>Average cost — last 10 of each type</h2>
        <div className="cost-tiles">
          {renderCostTile("recent-cost-rp", "RP videos", recentRpVideoCost, (r) => ({ average: r.summary.averageCostPerVideoUsd, count: r.summary.videoCount, unit: "video", partial: r.summary.partial, avgLengthSeconds: r.summary.averageDurationSeconds ?? null, ...(r.summary.durationVideoCount !== undefined ? { lengthCount: r.summary.durationVideoCount } : {}) }))}
          {renderCostTile("recent-cost-re", "RE videos", recentReVideoCost, (r) => ({ average: r.summary.averageCostPerVideoUsd, count: r.summary.videoCount, unit: "video", partial: r.summary.partial, avgLengthSeconds: r.summary.averageDurationSeconds ?? null, ...(r.summary.durationVideoCount !== undefined ? { lengthCount: r.summary.durationVideoCount } : {}) }))}
          {renderCostTile("recent-cost-images", "Images", recentImageSlotCost, (r) => ({ average: r.summary.averageCostPerImageUsd, count: r.summary.slotCount, unit: "slot", partial: r.summary.partial }))}
          {renderCostTile("recent-cost-dr", "DR runs", recentResearchCost, (r) => ({ average: r.summary.averageCostPerRunUsd, count: r.summary.runCount, unit: "run", partial: false }))}
        </div>
        {recentCostUpdatedAt !== null && (
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            updated {new Date(recentCostUpdatedAt).toLocaleString()}
          </p>
        )}
      </div>

      <AdminApiServiceSection />

      <Section title="FIX-050 · Verified Attribution (LinkScale → Fanvue)">
        <p className="muted">
          Manual only. Matches every stored Fanvue click <code>_sid</code> to a LinkScale <code>stats_id</code> using the canonical
          mapping. Safe to run again: it never duplicates or removes an existing attribution.
        </p>
        {attributionError && <div className="error-banner">{attributionError}</div>}
        <button type="button" onClick={() => void handleRunVerifiedAttribution()} disabled={attributionRunning}>
          {attributionRunning ? "Running…" : "Run Verified Attribution"}
        </button>
        {attributionResult && (
          <div className="success-banner" role="status" style={{ marginTop: 12 }}>
            <div>
              Run finished. Verified {attributionResult.verified} (new {attributionResult.newlyInserted}, already present{" "}
              {attributionResult.alreadyPresent}) · unmatched {attributionResult.unmatched} · conflicts {attributionResult.conflicts}
            </div>
            <table style={{ marginTop: 8 }}>
              <tbody>
                <tr><td>Candidates</td><td>{attributionResult.candidates}</td></tr>
                <tr><td>Distinct _sid</td><td>{attributionResult.distinctSids}</td></tr>
                <tr><td>Matched _sid</td><td>{attributionResult.matchedSids}</td></tr>
                <tr><td>Verified by bucket</td><td>{formatCounts(attributionResult.verifiedByBucket)}</td></tr>
                <tr><td>Conflicts by reason</td><td>{formatCounts(attributionResult.conflictsByReason)}</td></tr>
                <tr><td>Fanvue metadata rows</td><td>{attributionResult.dataQuality.fanvueMetadataRows}</td></tr>
                <tr><td>Rows with _sid</td><td>{attributionResult.dataQuality.metadataRowsWithSid}</td></tr>
                <tr><td>LinkScale click rows / flows</td><td>{attributionResult.dataQuality.linkscaleClickRows} / {attributionResult.dataQuality.distinctStatsIds}</td></tr>
                <tr><td>Flows with several rows (max rows per flow)</td><td>{attributionResult.dataQuality.statsIdsWithMultipleRows} ({attributionResult.dataQuality.maxRowsPerStatsId})</td></tr>
                <tr><td>Flows without a Fanvue _sid</td><td>{attributionResult.dataQuality.statsIdsWithoutFanvueSid}</td></tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Cost Comparison">
        {!costComparisonOpen ? (
          <button type="button" className="primary" onClick={handleOpenCostComparison}>
            Cost Comparison
          </button>
        ) : (
          <>
            {costComparisonError && <div className="error-banner">{costComparisonError}</div>}
            {costComparisonLoading || costComparison === null ? (
              <p className="muted">Loading…</p>
            ) : (
              <>
                <p className="muted">
                  FanStudio has no live API — its figures below are the exact example data Shai supplied by hand, not a
                  live measurement. See the Source column for exactly where every number comes from.
                </p>
                <table>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Video Studio</th>
                      <th>FanStudio</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Average cost per video</td>
                      <td>{formatUsd(costComparison.videoStudio.averageCostPerVideoUsd)}</td>
                      <td>{formatUsd(costComparison.fanStudio.nominalCostPerVideoUsd)}</td>
                    </tr>
                    <tr>
                      <td>Average duration</td>
                      <td>{formatSeconds(costComparison.videoStudio.averageDurationSeconds)}</td>
                      <td>{formatSeconds(costComparison.fanStudio.observedDurationSeconds)}</td>
                    </tr>
                    <tr>
                      <td>Cost per second</td>
                      <td>{formatUsd(costComparison.videoStudio.averageCostPerSecondUsd)}</td>
                      <td>{formatUsd(costComparison.fanStudio.nominalCostPerSecondUsd)}</td>
                    </tr>
                    <tr>
                      <td>Comparison difference (cost/second basis)</td>
                      <td colSpan={2}>{formatPercent(costComparison.comparison.percentageDifference)}</td>
                    </tr>
                    <tr>
                      <td>Cheaper option</td>
                      <td colSpan={2}>{cheaperSystemLabel(costComparison.comparison.cheaperSystem)}</td>
                    </tr>
                  </tbody>
                </table>
                <p className="muted" style={{ marginTop: 8 }}>
                  Last updated {new Date(costComparison.generatedAt).toLocaleString()} ·{" "}
                  {costComparison.videoStudio.population !== undefined ? (
                    <>
                      <strong>{costComparison.videoStudio.population.label}</strong> · refreshes every 30 seconds while this screen is open.
                    </>
                  ) : (
                    <>
                      Video Studio: {costComparison.videoStudio.includedVideoCount} finalized video
                      {costComparison.videoStudio.includedVideoCount === 1 ? "" : "s"} included in the calculation.
                    </>
                  )}
                </p>
                {costComparison.videoStudio.population?.partial === true && (
                  <p className="muted" role="note" style={{ fontSize: 12 }}>
                    Partial: {costComparison.videoStudio.population.partialVideoCount} of these videos {costComparison.videoStudio.population.partialVideoCount === 1 ? "has" : "have"} an
                    incomplete cost
                    {costComparison.videoStudio.population.missingDurationCount > 0 ? ` and ${costComparison.videoStudio.population.missingDurationCount} without a recorded duration` : ""}. Only known
                    costs are counted, so these figures understate the real cost; an unknown cost is never shown as $0.
                  </p>
                )}
                <p className="muted" style={{ fontSize: 12 }}>
                  Video Studio source: {costComparison.videoStudio.source}
                </p>
                <p className="muted" style={{ fontSize: 12 }}>
                  FanStudio source: {costComparison.fanStudio.source} Plan: {costComparison.fanStudio.planName} at{" "}
                  {formatUsd(costComparison.fanStudio.planPriceUsd)}/month for {costComparison.fanStudio.creditsPerMonth} credits; the
                  tested video used {costComparison.fanStudio.observedVideoCredits} credits. Receipt-based effective cost/credit (not used
                  in the comparison above): {formatUsd(costComparison.fanStudio.receiptEffectiveCostPerCreditUsd)} (paid{" "}
                  {formatUsd(costComparison.fanStudio.receiptAmountPaidUsd)} on {costComparison.fanStudio.receiptDatePaid}).
                </p>

                <h3>Weekly history</h3>
                <p className="muted" style={{ fontSize: 12 }}>
                  Saved snapshots record the average over all finalized videos (their own series, unchanged); they are not part of the live latest-10 figure above and are not re-read by its refresh.
                </p>
                {snapshotError && <div className="error-banner">{snapshotError}</div>}
                {snapshotSuccess && <div className="success-banner">{snapshotSuccess}</div>}
                <button type="button" onClick={() => void handleSaveCostComparisonSnapshot()} disabled={savingSnapshot} style={{ marginBottom: 12 }}>
                  {savingSnapshot ? "Saving…" : "Save this week's snapshot"}
                </button>
                {costComparisonHistory === null ? (
                  <p className="muted">Loading…</p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Video Studio avg cost/video</th>
                        <th>Video Studio cost/second</th>
                        <th>Videos included</th>
                        <th>FanStudio cost/video</th>
                        <th>Difference</th>
                        <th>Cheaper</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costComparisonHistory.map((row) => (
                        <tr key={row.id}>
                          <td>{row.snapshotDate}</td>
                          <td>{formatUsd(row.videoStudioAvgCostPerVideoUsd)}</td>
                          <td>{formatUsd(row.videoStudioCostPerSecondUsd)}</td>
                          <td>{row.videoStudioIncludedVideoCount}</td>
                          <td>{formatUsd(row.fanStudioCostPerVideoUsd)}</td>
                          <td>{formatPercent(row.percentageDifference)}</td>
                          <td>{cheaperSystemLabel(row.cheaperSystem)}</td>
                        </tr>
                      ))}
                      {costComparisonHistory.length === 0 && (
                        <tr>
                          <td colSpan={7} className="muted">
                            No snapshots saved yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </>
        )}
      </Section>

      <Section title="Memberships & Roles">
        <p className="muted">Manage a specific workspace's members and roles from that workspace's own WORKSPACE tab (invite, change role, remove) — reachable via the workspace switcher above.</p>
      </Section>

      <Section title="Plans & Entitlements">
        {plansNote && <p className="muted">{plansNote}</p>}
        {plans === null ? (
          <p className="muted">Loading…</p>
        ) : plans.length === 0 ? (
          <NotConfigured label="No plans provisioned yet" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Plan</th>
                <th>Entitlements</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td style={{ fontSize: 12 }}>{JSON.stringify(p.entitlements)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Licenses">
        {licenses === null ? (
          <NotConfigured label="License listing" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {licenses.map((l) => (
                <tr key={l.tenantId}>
                  <td>{l.workspaceName}</td>
                  <td>{l.planName ?? <span className="muted">none</span>}</td>
                  <td>{l.status ?? <span className="muted">unlicensed</span>}</td>
                  <td>{l.startedAt ? new Date(l.startedAt).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {licenses.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    No GENERATE workspaces yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        <h3>Assign a plan</h3>
        <p className="muted">No Stripe/Paddle integration yet — this is the manual OWNER-controlled assignment.</p>
        {assignError && <div className="error-banner">{assignError}</div>}
        {assignSuccess && <div className="success-banner">{assignSuccess}</div>}
        <form onSubmit={(event) => void handleAssignLicense(event)}>
          <div className="grid grid-3">
            <div className="field">
              <label htmlFor="assignTenantId">Workspace tenant ID</label>
              <select id="assignTenantId" required value={assignTenantId} onChange={(event) => setAssignTenantId(event.target.value)}>
                <option value="">Select…</option>
                {workspaces?.map((w) => (
                  <option key={w.tenantId} value={w.tenantId}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="assignPlanId">Plan</label>
              <select id="assignPlanId" required value={assignPlanId} onChange={(event) => setAssignPlanId(event.target.value)}>
                <option value="">Select…</option>
                {plans?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="assignStatus">Status</label>
              <select id="assignStatus" value={assignStatus} onChange={(event) => setAssignStatus(event.target.value as typeof assignStatus)}>
                <option value="trialing">trialing</option>
                <option value="active">active</option>
                <option value="past_due">past_due</option>
                <option value="canceled">canceled</option>
              </select>
            </div>
          </div>
          <button type="submit" className="primary" disabled={assigning}>
            {assigning ? "Assigning…" : "Assign license"}
          </button>
        </form>
      </Section>

      <Section title="Usage">
        {usage === null ? (
          <NotConfigured label="Cross-workspace usage rollups" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Workspace</th>
                <th>This month</th>
                <th>By kind</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((row) => (
                <tr key={row.tenantId}>
                  <td>{row.workspaceName}</td>
                  <td>{row.totalQuantity}</td>
                  <td style={{ fontSize: 12 }}>{JSON.stringify(row.byKind)}</td>
                  <td>{row.totalCostUsd === null ? <span className="muted">untracked</span> : `$${row.totalCostUsd.toFixed(2)}`}</td>
                </tr>
              ))}
              {usage.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    No GENERATE workspaces yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Models">
        {models === null ? (
          <NotConfigured label="Model catalog" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th>Type</th>
                <th>Base price</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.modelId}>
                  <td>{m.name}</td>
                  <td>{m.type ?? <span className="muted">—</span>}</td>
                  <td>{m.basePrice === null ? <span className="muted">—</span> : `$${m.basePrice}`}</td>
                  <td style={{ fontSize: 12 }}>{m.description ?? <span className="muted">—</span>}</td>
                </tr>
              ))}
              {models.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    No models available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Personas">
        <p className="muted">Inspect any workspace's persona readiness — active reference count and which identity strategies are actually available.</p>
        <form onSubmit={(event) => void handleLoadPersonas(event)}>
          <div className="grid grid-2">
            <div className="field">
              <label htmlFor="personaWorkspaceId">Workspace</label>
              <select id="personaWorkspaceId" required value={personaWorkspaceId} onChange={(event) => setPersonaWorkspaceId(event.target.value)}>
                <option value="">Select…</option>
                {workspaces?.map((w) => (
                  <option key={w.tenantId} value={w.tenantId}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="primary">
            Inspect personas
          </button>
        </form>
        {personasError && <div className="error-banner">{personasError}</div>}
        {personas !== null && (
          <table>
            <thead>
              <tr>
                <th>Persona</th>
                <th>Active</th>
                <th>Reference images</th>
                <th>Identity strategies</th>
                <th>Ready</th>
                <th>Phota state</th>
                <th>Admin status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {personas.map((p) => (
                <tr key={p.personaId}>
                  <td>{p.displayName}</td>
                  <td>{p.active ? "Yes" : "No"}</td>
                  <td>{p.activeReferenceCount}</td>
                  <td>{p.availableIdentityStrategies.length > 0 ? p.availableIdentityStrategies.join(", ") : <span className="muted">none</span>}</td>
                  <td>{p.ready ? "Yes" : "No"}</td>
                  <td>
                    {p.photaReadinessState !== null ? (PHOTA_READINESS_STATE_LABEL[p.photaReadinessState] ?? p.photaReadinessState) : <span className="muted">n/a</span>}
                    {p.photaProfileId !== null && <div className="muted">{p.photaProfileId}</div>}
                  </td>
                  <td>
                    {p.adminStatus === "suspended" ? (
                      <span className="badge status-rejected">Suspended / Paused</span>
                    ) : (
                      <span className="badge status-approved">Active</span>
                    )}
                    {p.suspensionReason !== null && <div className="muted">{p.suspensionReason}</div>}
                  </td>
                  <td>
                    {p.adminStatus === "suspended" ? (
                      <button type="button" onClick={() => void handlePersonaStatusAction(p.personaId, "reactivate")}>
                        Reactivate
                      </button>
                    ) : (
                      <button type="button" onClick={() => void handlePersonaStatusAction(p.personaId, "suspend")}>
                        Suspend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {personas.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    No personas in this workspace.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Section>

      {/* FIX-011 / RE Persona Voice: standalone section, no fetch on mount. */}
      <PersonaVoiceSection workspaces={workspaces} csrfToken={csrfToken} />

      <Section title="Audit Log">
        {auditLog === null ? (
          <p className="muted">Loading…</p>
        ) : auditLog.length === 0 ? (
          <p className="muted">No audit entries yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.map((entry) => (
                <tr key={entry.id}>
                  <td>{new Date(entry.createdAt).toLocaleString()}</td>
                  <td>{entry.actorUserId ?? "system"}</td>
                  <td>{entry.action}</td>
                  <td>
                    {entry.resourceType}
                    {entry.resourceId ? `/${entry.resourceId}` : ""}
                  </td>
                  <td>{entry.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="System">
        <p className="muted">See GENERATE → MONITOR for live system health.</p>
      </Section>
    </div>
  );
}
